import React, { useEffect, useMemo, useState } from "react";
import HelpCenterCard from "./HelpCenterCard";
import { FaSearch } from "react-icons/fa";
import axios from "../../API/axios";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import useThemeStore from "../../store/useThemeStore";

const HelpCenterCards = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState("");
  const [categories, setCategories] = useState([]);
  const [articles, setArticles] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const isDark = useThemeStore((state) => state.isDark);

  useEffect(() => {
    const script = document.createElement("script");
    script.async = true;
    script.src =
      "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-8691062301030113";
    script.crossOrigin = "anonymous";
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadHelpCenter = async () => {
      setIsLoading(true);
      try {
        const [categoriesRes, articlesRes] = await Promise.all([
          axios.get("HelpCenter/Categories?DeviceType=Web"),
          axios.get("HelpCenter/Articles?DeviceType=Web"),
        ]);
        if (cancelled) return;
        const categoryList =
          categoriesRes.data?.responseData ||
          categoriesRes.data?.ResponseData ||
          [];
        const articleList =
          articlesRes.data?.responseData ||
          articlesRes.data?.ResponseData ||
          [];
        setCategories(Array.isArray(categoryList) ? categoryList : []);
        setArticles(Array.isArray(articleList) ? articleList : []);
      } catch (error) {
        if (!cancelled) {
          setCategories([]);
          setArticles([]);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    loadHelpCenter();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredArticles = useMemo(() => {
    const query = searchText.trim().toLowerCase();
    return articles.filter((article) => {
      const categoryId = article.hccid ?? article.Hccid;
      const title = (article.title ?? article.Title ?? "").toLowerCase();
      const shortDescription = (
        article.shortDescription ??
        article.ShortDescription ??
        ""
      ).toLowerCase();
      const matchesCategory =
        selectedCategory === "all" ||
        String(categoryId) === String(selectedCategory);
      const matchesSearch =
        !query || title.includes(query) || shortDescription.includes(query);
      return matchesCategory && matchesSearch;
    });
  }, [articles, selectedCategory, searchText]);

  const suggestionNames = categories
    .slice(0, 5)
    .map((c) => c.name ?? c.Name ?? c.hpcategoryName)
    .filter(Boolean);

  return (
    <>
      <div
        className={`helpCenterTab d-flex align-items-center justify-content-center flex-column ${
          isDark ? "dark-mode" : ""
        }`}
      >
        <h2 className="help-search-heading">Hello, May I Help You?</h2>
        <div className="help-search-container">
          <div className="help-search-box">
            <select
              className="category-dropdown"
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <option value="all">All Categories</option>
              {categories.map((category) => {
                const id = category.hccid ?? category.Hccid;
                const name =
                  category.name ?? category.Name ?? category.hpcategoryName;
                return (
                  <option key={id} value={id}>
                    {name}
                  </option>
                );
              })}
            </select>
            <input
              type="search"
              className="helpcenter-search"
              placeholder={t("Common.TypeYourQuestion")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
            />
            <button className="search-button" type="button">
              <FaSearch />
            </button>
          </div>
        </div>
        <div className="helpcenter-categories">
          <span>Suggestion :</span>
          <ul>
            {suggestionNames.length > 0
              ? suggestionNames.map((name) => <li key={name}>{name}</li>)
              : ["Slider", "Tutorial", "HTML5", "CSS 3", "Prototyping"].map(
                  (name) => <li key={name}>{name}</li>
                )}
          </ul>
        </div>
      </div>

      <div className="container py-4">
        <h4 className="mb-3">{t("HelpCenter.HelpArticles")}</h4>
        {isLoading && <p className="text-muted">{t("HelpCenter.LoadingArticles")}</p>}
        {!isLoading && filteredArticles.length === 0 && (
          <p className="text-muted">{t("HelpCenter.NoHelpArticles")}</p>
        )}
        <div className="row">
          {filteredArticles.map((article) => {
            const id = article.hcid ?? article.Hcid;
            const title = article.title ?? article.Title;
            const shortDescription =
              article.shortDescription ?? article.ShortDescription;
            return (
              <div className="col-md-6 col-lg-4 mb-3" key={id}>
                <Link
                  to={`/HelpCenterDetailPage?id=${id}`}
                  className="text-decoration-none text-dark"
                >
                  <div className="border rounded p-3 h-100">
                    <h5 className="mb-2">{title}</h5>
                    <p className="mb-0 text-muted small">
                      {shortDescription || "View article"}
                    </p>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      </div>

      <div className="helpCenterCard">
        <HelpCenterCard tab={tab} />
      </div>
    </>
  );
};

export default HelpCenterCards;
